import React from 'react';
import { usePageMeta } from '../../seo/usePageMeta';
import SeoPageLayout from './SeoPageLayout';
import GameplayScreenshots from './GameplayScreenshots';
import GameSetupCta from './GameSetupCta';
import { alternatesFor } from '../../seo/translatedPages';

const FAQS = [
    {
        q: 'What is Among Us IRL?',
        a: 'It is an Among Us-inspired game in a real space. Your house becomes the map: crew members complete physical tasks, intruders try to stay hidden, and the group meets to discuss and vote. Sus Party runs the roles, task lists, and votes in phone browsers.',
    },
    {
        q: 'How many players do we need?',
        a: 'Anywhere from 3 to 20+. The game requires at least two crew members per intruder, so one intruder needs at least three players. For a first game, four to six players with one intruder is an easy start. The task generator prepares groups of three to thirty.',
    },
    {
        q: 'Is Sus Party free? Do we need to download an app?',
        a: 'It is free, with no ads, account, or download. Everyone opens susparty.com in a phone browser and joins with the room code.',
    },
    {
        q: 'How do you play Among Us in real life?',
        a: 'Most players are crew and complete real tasks around the house. One or more secret intruders fake tasks and eliminate crew members in private. Anyone can call a meeting to argue and vote someone out. The crew wins by finishing tasks and voting out every intruder; the intruders win by outnumbering the crew.',
    },
    {
        q: 'Can we play without an app?',
        a: 'Yes. Use paper roles, a task list, a timer, and a clear vote method. Sus Party uses each phone to track roles, tasks, meetings, and votes.',
    },
    {
        q: 'Is Sus Party an official Among Us product?',
        a: 'No. Sus Party is an independent fan game. It is not affiliated with or endorsed by Innersloth.',
    },
];

const SCHEMA = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'SoftwareApplication',
            name: 'Sus Party',
            applicationCategory: 'Game',
            operatingSystem: 'Any (browser-based)',
            description:
                'Sus Party is a free browser game for in-person social deduction with private roles, task lists, meetings, and votes, for 3 to 20+ players.',
            url: 'https://susparty.com',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        },
        {
            '@type': 'FAQPage',
            mainEntity: FAQS.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
        },
    ],
};

function AmongUsIrlPage() {
    usePageMeta({
        title: 'Among Us IRL: Free App for 3 to 20+ Players | Sus Party',
        description:
            'Play Among Us in real life for free. Phones deal secret roles, real tasks, meetings and votes for 3 to 20+ players at home, at school, or at a youth group. No install.',
        canonical: 'https://susparty.com/among-us-irl',
        ogImage: 'https://susparty.com/og-image.jpg',
        schema: SCHEMA,
        alternates: alternatesFor('irl'),
    });

    return (
        <SeoPageLayout languagePage="irl">
            <div className="seo-hero">
                <p className="seo-eyebrow">Sus Party · Free browser game</p>
                <h1 className="seo-h1">Play Among Us in real life with Sus Party</h1>
                <p className="seo-lead">
                    Someone heads to the kitchen for a task. Someone follows them a little too closely.
                    A meeting starts, and suddenly everyone has an alibi. Sus Party is the free Among Us IRL app
                    that turns your rooms into the map and your friends into the suspects.
                    It works for 3 players or 20+: a family night, a birthday, a class, or a whole youth group.
                </p>
                <div className="seo-actions">
                    <a href="/among-us-irl-task-generator?venue=house&players=8&movement=normal" className="seo-btn--primary">
                        Build your first game →
                    </a>
                    <a href="/play" className="seo-btn--secondary">
                        Create or join a room
                    </a>
                </div>
            </div>

            <div className="seo-content">
                <nav className="seo-links" aria-label="Jump to a setup guide">
                    <p className="seo-links__title">Choose a venue guide</p>
                    <ul className="seo-links__list">
                        <li><a href="#classroom-setup">Classroom setup</a></li>
                        <li><a href="#vacation-house-setup">Vacation-house setup</a></li>
                        <li><a href="#other-venues">Apartment, dorm, or office</a></li>
                        <li><a href="#big-groups">Big groups of 20+</a></li>
                    </ul>
                </nav>

                <section className="seo-section" id="quick-start">
                    <p className="seo-section__kicker">Quick start</p>
                    <h2>Your house is the spaceship. Phones run the game.</h2>
                    <p>
                        Everyone opens a phone browser in the same place. Sus Party supplies private roles,
                        task lists, sabotage cards, and votes. You supply the rooms and the conversation.
                        No account, download, or dedicated game board is required. Each device needs internet.
                    </p>
                    <ol className="seo-steps" aria-label="Quick setup steps">
                        <li className="seo-step">
                            <span className="seo-step__num" aria-hidden="true">1</span>
                            <div>
                                <h3>Make your map</h3>
                                <p>Use the living room, dining area, and other shared spaces. Choose one place to meet and keep private rooms out of play.</p>
                            </div>
                        </li>
                        <li className="seo-step">
                            <span className="seo-step__num" aria-hidden="true">2</span>
                            <div>
                                <h3>Pick a task pack</h3>
                                <p>Open the generator, select your rooms, and review the tasks. Change any item that does not fit your space.</p>
                            </div>
                        </li>
                        <li className="seo-step">
                            <span className="seo-step__num" aria-hidden="true">3</span>
                            <div>
                                <h3>Take it into Sus Party</h3>
                                <p>Select “Use this pack.” Sus Party creates your room with the tasks already loaded.</p>
                            </div>
                        </li>
                        <li className="seo-step">
                            <span className="seo-step__num" aria-hidden="true">4</span>
                            <div>
                                <h3>Share the code and deal the roles</h3>
                                <p>Friends join with the four-letter code. Explain the meeting point and round rules, then start the game.</p>
                            </div>
                        </li>
                    </ol>
                    <div className="seo-highlight">
                        <p>
                            Try the <a href="/tutorial">crew tutorial</a> before your first round.
                            A phone-only game works. A larger screen can also join as the optional Reactor.
                        </p>
                    </div>
                </section>

                <section className="seo-section">
                    <p className="seo-section__kicker">What the app handles</p>
                    <h2>What happens on your phone</h2>
                    <div className="seo-cards seo-cards--3">
                        <div className="seo-card">
                            <h3>Roles and tasks</h3>
                            <p>Your role stays on your own screen. Crew members see a task and its location, do it in the room, then slide to complete it.</p>
                        </div>
                        <div className="seo-card">
                            <h3>Meetings and votes</h3>
                            <p>A living player calls a meeting. Everyone gathers to compare stories, then votes on their phone. The app displays live vote totals.</p>
                        </div>
                        <div className="seo-card">
                            <h3>Sabotage and the reveal</h3>
                            <p>Intruders use cards to disrupt the crew. At the task goal, the app reveals living intruders. The crew still has to vote them out.</p>
                        </div>
                    </div>
                </section>

                <section className="seo-section" id="rules">
                    <p className="seo-section__kicker">The rules</p>
                    <h2>How to play Among Us in real life</h2>
                    <ul className="seo-list">
                        <li><strong>Crew:</strong> most players. Walk to each task on your phone, do it in the room, and slide to finish it.</li>
                        <li><strong>Intruders:</strong> one or more secret players. They fake tasks, use sabotage cards, and eliminate crew members when nobody is watching.</li>
                        <li><strong>Meetings:</strong> a crew member calls one from their phone. Everyone gathers at the meeting point, shares what they saw, and votes.</li>
                        <li><strong>Winning:</strong> the crew wins by finishing tasks and voting out every intruder. The intruders win once they match the crew in number.</li>
                    </ul>
                    <p>
                        A first round takes 10 to 20 minutes. Read the full <a href="/how-to-play-among-us-irl">rules and a sample round</a>,
                        or deal paper roles with the <a href="/among-us-role-generator">Among Us role generator</a>.
                    </p>
                </section>

                <GameplayScreenshots screens={['crew-task', 'intruder-cards']} />

                <GameSetupCta title="See the task pack before you invite anyone"
                    href="/among-us-irl-task-generator?venue=house&players=8&movement=normal" label="Preview an eight-player house game">
                    A sample list is ready in the generator. Change the rooms and player count, review the tasks, then carry the pack into a new game.
                </GameSetupCta>

                <section className="seo-section" id="classroom-setup">
                    <p className="seo-section__kicker">Classroom guide</p>
                    <h2>Set up Among Us IRL in a classroom</h2>
                    <p>
                        Turn the supply table, board, and desks into task stations. The school preset supplies a starting list;
                        the host chooses which tasks fit the group. Confirm permission to use phones and the space before the round.
                    </p>
                    <ul className="seo-list">
                        <li>Set tasks at the supply table, board, or desks.</li>
                        <li>Prepare shared materials such as markers, index cards, and task slips.</li>
                        <li>Keep play inside approved rooms and use a step-free route.</li>
                    </ul>
                    <p>
                        For example, count markers in the classroom supply cup.
                        Browse <a href="/among-us-irl-task-ideas#classroom-tasks">classroom task examples</a>
                        {' '}or <a href="/among-us-irl-task-generator?venue=school&players=8&movement=normal">build an eight-player classroom task list</a>.
                    </p>
                </section>

                <section className="seo-section" id="vacation-house-setup">
                    <p className="seo-section__kicker">Vacation-house guide</p>
                    <h2>Set up a vacation-house game</h2>
                    <p>
                        The shared living room becomes one location, the dining table another.
                        Use the vacation-house preset to build a task list around the space you actually have.
                        Put out cards, coasters, and paper before the round so guests know what belongs to the game.
                    </p>
                    <ul className="seo-list">
                        <li>Keep private rooms and host-excluded areas outside the play boundary.</li>
                        <li>Use visible shared items, then return each item to its place.</li>
                        <li>Choose nearby locations for a short, step-free route.</li>
                    </ul>
                    <p>
                        For example, count the cushions on the shared living-room sofa.
                        See <a href="/among-us-irl-task-ideas#vacation-house-tasks">vacation-house task examples</a>
                        {' '}or <a href="/among-us-irl-task-generator?venue=airbnb&players=8&movement=normal">build an eight-player vacation-rental task list</a>.
                    </p>
                </section>

                <section className="seo-section" id="other-venues">
                    <p className="seo-section__kicker">Other venues</p>
                    <h2>Choose a task preset for your space</h2>
                    <p>Use a preset as a draft. Remove any task that does not fit your actual room or access rules.</p>
                    <div className="seo-cards seo-cards--2">
                        <div className="seo-card">
                            <h3>House or apartment</h3>
                            <p>Use shared rooms such as a living room, dining area, and approved hallway.</p>
                            <a href="/among-us-irl-task-generator?venue=house&players=8&movement=normal">Generate house tasks →</a>
                            {' · '}
                            <a href="/among-us-irl-task-generator?venue=apartment&players=8&movement=normal">Apartment tasks →</a>
                        </div>
                        <div className="seo-card">
                            <h3>Dorm or office</h3>
                            <p>Choose common rooms and exclude private rooms, work areas, and restricted spaces.</p>
                            <a href="/among-us-irl-task-generator?venue=dorm&players=8&movement=normal">Generate dorm tasks →</a>
                            {' · '}
                            <a href="/among-us-irl-task-generator?venue=office&players=8&movement=normal">Office tasks →</a>
                        </div>
                    </div>
                    <p style={{ marginTop: 16 }}>
                        Need a different layout? <a href="/among-us-irl-task-generator?venue=other&players=8&movement=normal">Generate tasks for another venue</a>.
                    </p>
                </section>

                <section className="seo-section" id="big-groups">
                    <p className="seo-section__kicker">Big groups</p>
                    <h2>Play with 20 or more people</h2>
                    <p>
                        One Sus Party room can hold a whole youth group, class, or party.
                        A few things keep a big game moving:
                    </p>
                    <ul className="seo-list">
                        <li>Open susparty.com/play on a laptop, TV, or projector and create the room there. It becomes the big screen and shows the room code to everyone.</li>
                        <li>Players type susparty.com on their phones and enter the code. The first phone to join is the host and starts the game.</li>
                        <li>Use more intruders as the group grows, and plan more tasks than players so nobody stands around.</li>
                        <li>Pick a meeting point with room for everyone, and spread tasks across several areas.</li>
                    </ul>
                    <p>
                        Running it for a youth group, library, or school? See the <a href="/among-us-irl-for-youth-groups">youth group and classroom guide</a>.
                    </p>
                </section>

                <section className="seo-section">
                    <p className="seo-section__kicker">FAQ</p>
                    <h2>Among Us IRL questions</h2>
                    <div className="seo-faq">
                        {FAQS.map((faq) => (
                            <div className="seo-faq__item" key={faq.q}>
                                <p className="seo-faq__q">{faq.q}</p>
                                <p>{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <nav className="seo-links" aria-label="Related Among Us IRL guides">
                    <p className="seo-links__title">Continue with a guide</p>
                    <ul className="seo-links__list">
                        <li><a href="/how-to-play-among-us-irl">How to play: rules and sample round</a></li>
                        <li><a href="/among-us-irl-task-ideas">Task ideas by venue and movement</a></li>
                        <li><a href="/among-us-irl-task-generator?venue=house&players=8&movement=normal">Make a task list for your space</a></li>
                        <li><a href="/among-us-role-generator">Deal secret roles with the role generator</a></li>
                        <li><a href="/among-us-irl-for-youth-groups">Run it for a youth group or class</a></li>
                        <li><a href="/among-us-birthday-party">Make it the main birthday activity</a></li>
                        <li><a href="/social-deduction-games">Compare social deduction games for your group</a></li>
                    </ul>
                </nav>
            </div>
        </SeoPageLayout>
    );
}

export default AmongUsIrlPage;
